import React, { useState, useEffect, useCallback } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCheckCircle,
  FaSearch,
} from "react-icons/fa";
import { Modal, Button } from "react-bootstrap";
import axios from "axios";
import debounce from "lodash/debounce";
import PropTypes from "prop-types";
import Apiurl from "../../environment/Apiurl";

const Model = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [models, setModels] = useState([]);
  const [brands, setBrands] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5); // Treated as number
  const [editModel, setEditModel] = useState(null);
  const [deleteModelId, setDeleteModelId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Debounced search handler
  const handleSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 300),
    []
  );

  const [privileges, setPrivileges] = useState(null);

  // Fetch privileges from localStorage
  useEffect(() => {
    const getaccess = localStorage.getItem("role");
    setPrivileges(getaccess);
  }, []);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({
      top: 0,
      behavior: "smooth", // optional for smooth scrolling
    });
  }, []);

  // Fetch all models and brands from API
  useEffect(() => {
    fetchModels();
    fetchBrands();
  }, []);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${Apiurl}/model`);
      setModels(response.data.reverse());
      setLoading(false);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to fetch models. Please try again.";
      setError(errorMessage);
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${Apiurl}/brand`);
      setBrands(response.data);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to fetch brands. Please try again.";
      setError(errorMessage);
    }
  };

  // Handle modal open/close
  const handleCloseAddModal = () => setShowAddModal(false);
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditModel(null);
  };
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteModelId(null);
  };

  // Handle adding a model
  const handleAddModel = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.target;
    const modelData = {
      model_name: form.modelName.value,
      brand_name: form.brand_name.value,
      status: form.modelStatus.value,
    };

    try {
      const response = await axios.post(`${Apiurl}/model`, modelData);
      setModels([...models, response.data]);
      form.reset();
      handleCloseAddModal();
      setError(null);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to add model. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle editing a model
  const handleEditModel = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.target;
    const modelData = {
      model_name: form.modelName.value,
      brand_name: form.brand_name.value,

      status: form.modelStatus.value,
    };

    try {
      const response = await axios.put(
        `${Apiurl}/model/${editModel._id}`,
        modelData
      );
      setModels(
        models.map((model) =>
          model._id === editModel._id ? response.data : model
        )
      );
      form.reset();
      handleCloseEditModal();
      setError(null);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update model. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete model
  const handleDeleteModel = (id) => {
    setDeleteModelId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsSubmitting(true);
    try {
      await axios.delete(`${Apiurl}/model/${deleteModelId}`);
      setModels(models.filter((model) => model._id !== deleteModelId));
      handleCloseDeleteModal();
      setError(null);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to delete model. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle pagination
  const filteredModels = models.filter(
    (model) =>
      model.model_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (model.brand_name &&
        model.brand_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (model.year && model.year.toString().includes(searchTerm)) ||
      (model.specifications &&
        model.specifications.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentModels = filteredModels.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredModels.length / itemsPerPage);

  // Generate pagination with ellipsis logic
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return (
      <div
        className="container-fluid p-3 d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-3">
      {/* Error Alert */}
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Header */}
      <div className="d-flex justify-content-end mb-3">
        {(privileges === "Admin" ||
          privileges === "Manager" ||
          privileges === "Receptionist") && (
          <Button
            variant="primary"
            size="sm"
            className="btn btn-sm text-white"
            style={{ backgroundColor: "#2e9290" }}
            onClick={() => setShowAddModal(true)}
          >
            <FaPlus className="me-2" />
            Add Model
          </Button>
        )}
      </div>

      {/* Main Table Card */}
      <div className="card shadow-sm">
        <div className="card-body">
          {/* Controls */}
          <div className="d-flex flex-wrap mb-2">
            <div className="w-full md:w-2/12 mb-2 md:mb-0">
              <div className="position-relative">
                <span className="position-absolute top-50 start-0 translate-middle-y ps-3">
                  <i className="fas fa-table text-gray-500"></i>
                </span>
                <select
                  className="form-select ps-10 w-100 border-gray-300 rounded-md"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setCurrentPage(1);
                    setItemsPerPage(Number(e.target.value));
                  }}
                >
                  {[5, 10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      Show {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="w-full md:w-7/12"></div>
            <div className="w-full md:w-3/12">
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control w-100 ps-3 pe-10 py-2 border-gray-300 rounded-md"
                  placeholder="Search"
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <FaSearch className="text-gray-400 position-absolute end-0 top-50 translate-middle-y pe-3" />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="table w-100">
              <thead className="bg-teal-600 text-white">
                <tr>
                  <th
                    scope="col"
                    className="py-2 px-4"
                    style={{ backgroundColor: "#2e9290", color: "white" }}
                  >
                    S.No
                  </th>
                  <th
                    scope="col"
                    className="py-2 px-4"
                    style={{ backgroundColor: "#2e9290", color: "white" }}
                  >
                    Model
                  </th>
                  <th
                    scope="col"
                    className="py-2 px-4"
                    style={{ backgroundColor: "#2e9290", color: "white" }}
                  >
                    Brand
                  </th>

                  <th
                    scope="col"
                    className="py-2 px-4"
                    style={{ backgroundColor: "#2e9290", color: "white" }}
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="py-2 px-4"
                    style={{ backgroundColor: "#2e9290", color: "white" }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentModels.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      {searchTerm
                        ? "No models found matching your search"
                        : "No models available"}
                    </td>
                  </tr>
                ) : (
                  currentModels.map((model, index) => (
                    <tr
                      key={model._id}
                      className="align-middle hover:bg-gray-50"
                    >
                      <td className="py-2 px-4">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="py-2 px-4">{model.model_name}</td>
                      <td className="py-2 px-4">{model.brand_name}</td>

                      <td className="py-2 px-4">{model.status}</td>

                      <td className="py-2 px-4 d-flex gap-2">
                        {(privileges === "Admin" ||
                          privileges === "Manager") && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 border-0 bg-transparent text-blue-500 hover:text-blue-700"
                            onClick={() => {
                              setEditModel(model);
                              setShowEditModal(true);
                            }}
                          >
                            <FaEdit />
                          </Button>
                        )}
                        {privileges === "Admin" && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 border-0 bg-transparent text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteModel(model._id)}
                          >
                            <FaTrash className="text-danger" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="container">
              <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                <Button
                  variant="outline-secondary"
                  className="d-flex align-items-center gap-2"
                  onClick={() => handlePageClick(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                  <span className="d-none d-sm-inline">Previous</span>
                </Button>
                <div className="d-flex overflow-x-auto flex-nowrap gap-2 px-2">
                  {pages.map((page, index) => (
                    <Button
                      key={index}
                      variant={
                        page === currentPage ? "primary" : "outline-secondary"
                      }
                      size="sm"
                      className="px-3 flex-shrink-0"
                      style={
                        page === currentPage
                          ? { backgroundColor: "#0d9488", border: "none" }
                          : {}
                      }
                      onClick={() => handlePageClick(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="outline-secondary"
                  className="d-flex align-items-center gap-2"
                  onClick={() => handlePageClick(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <span className="d-none d-sm-inline">Next</span>
                  <i className="fas fa-chevron-right"></i>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Model Modal */}
      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        centered
        aria-labelledby="addModelTitle"
      >
        <Modal.Header closeButton>
          <Modal.Title id="addModelTitle">Add Model</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleAddModel}>
            <div className="mb-3">
              <label className="form-label">Model Name</label>
              <input
                type="text"
                name="modelName"
                className="form-control border-gray-300 rounded-md"
                placeholder="e.g., iPhone 13 Pro"
                required
              />
            </div>
            <div className="d-flex flex-wrap -mx-2">
              <div className="w-100 w-md-50 px-2 mb-3">
                <label className="form-label">Brand</label>
                <select
                  name="brand_name"
                  className="form-select border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select Brand</option>
                  {brands?.length > 0 ? (
                    brands.map((brand) => (
                      <option key={brand._id} value={brand.brand_name}>
                        {brand.brand_name}
                      </option>
                    ))
                  ) : (
                    <option value="">No brands available</option>
                  )}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                name="modelStatus"
                className="form-select border-gray-300 rounded-md"
                defaultValue="Active"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={handleCloseAddModal}
                disabled={isSubmitting}
              >
                Close
              </Button>
              <Button
                type="submit"
                className="btn btn-sm text-white"
                style={{ backgroundColor: "#2e9290" }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Adding..." : "Add Model"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Edit Model Modal */}
      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        centered
        aria-labelledby="editModelTitle"
      >
        <Modal.Header closeButton>
          <Modal.Title id="editModelTitle">Edit Model</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleEditModel}>
            <div className="mb-3">
              <label className="form-label">Model Name</label>
              <input
                type="text"
                name="modelName"
                className="form-control border-gray-300 rounded-md"
                defaultValue={editModel ? editModel.model_name : ""}
                placeholder="e.g., iPhone 13 Pro"
                required
              />
            </div>
            <div className="d-flex flex-wrap -mx-2">
              <div className="w-100 w-md-50 px-2 mb-3">
                <label className="form-label">Brand</label>
                <select
                  name="brand_name"
                  className="form-select border-gray-300 rounded-md"
                  defaultValue={editModel ? editModel.brand_name : ""}
                  required
                >
                  <option value="">Select Brand</option>
                  {brands?.length > 0 ? (
                    brands.map((brand) => (
                      <option key={brand._id} value={brand.brand_name}>
                        {brand.brand_name}
                      </option>
                    ))
                  ) : (
                    <option value="">No brands available</option>
                  )}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                name="modelStatus"
                className="form-select border-gray-300 rounded-md"
                defaultValue={editModel ? editModel.status : "Active"}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={handleCloseEditModal}
                disabled={isSubmitting}
              >
                Close
              </Button>
              <Button
                type="submit"
                className="btn btn-sm text-white"
                style={{ backgroundColor: "#2e9290" }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Updating..." : "Update Model"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        centered
        aria-labelledby="deleteModelTitle"
      >
        <Modal.Header closeButton>
          <Modal.Title id="deleteModelTitle">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the model{" "}
            {models.find((m) => m._id === deleteModelId)?.model_name}?
          </p>
          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              onClick={handleCloseDeleteModal}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Model;
